/**
 * Sales Agent API Endpoints
 * Handles conversion tracking, event logging, and paywall personalization
 */

import apiClient from '../client';

// ========================================
// TYPES
// ========================================

export type SalesEventType =
  | 'paywall_hit'
  | 'pricing_page_viewed'
  | 'feature_blocked'
  | 'diagnostic_completed'
  | 'goal_created'
  | 'arbitrage_found'
  | 'message_limit_reached';

export interface InAppModal {
  type: string;
  title: string;
  subtitle?: string;
  description?: string;
  benefits?: string[];
  cta_label: string;
  cta_action: string;
  premium_only?: boolean;
}

export interface TrackEventResponse {
  success: boolean;
  event_id: string;
  in_app_modal?: InAppModal;
  sales_action_id?: string;
}

export interface ActionClickedResponse {
  success: boolean;
}

export interface ConversionAttribution {
  action_type: string;
  template: string;
  sent_at: string;
}

export interface TrackConversionResponse {
  success: boolean;
  attributed: boolean;
  attribution: ConversionAttribution | null;
}

export interface ValueProposition {
  icon: string;
  title: string;
  description: string;
}

export interface UsageStats {
  messages_used: number;
  messages_limit: number;
  diagnostics_used: number;
  diagnostics_limit: number;
  goals_count: number;
  goals_limit: number;
}

export interface PotentialGains {
  arbitrage_annual: number;
  optimization_potential: number | null;
}

export interface SocialProof {
  users_converted_this_month: number;
  avg_savings_found: number;
}

export interface Pricing {
  monthly_price: number;
  annual_price: number;
  currency: string;
  discount_percentage: number;
}

export interface Urgency {
  type: string;
  message: string;
  expires_in_hours: number;
}

export interface PaywallData {
  user: {
    first_name: string | null;
    plan: string;
    is_premium: boolean;
  };
  value_propositions: ValueProposition[];
  usage_stats: UsageStats;
  potential_gains: PotentialGains | null;
  social_proof: SocialProof;
  pricing: Pricing;
  urgency: Urgency | null;
}

export interface PaywallDataResponse {
  success: boolean;
  data: PaywallData;
}

export interface LeadScoreComponents {
  engagement: number;
  intent: number;
  profile_fit: number;
  recency: number;
}

export interface LeadScoreResponse {
  success: boolean;
  score: number;
  segment: 'cold' | 'warm' | 'hot' | 'burning';
  components: LeadScoreComponents;
  triggers: string[];
}

// ========================================
// API ENDPOINTS
// ========================================

export const salesApi = {
  /**
   * Track a sales/conversion event
   * Call this when user hits a paywall, views pricing, etc.
   * Returns in_app_modal data if a modal should be shown
   */
  trackEvent: async (
    eventType: SalesEventType,
    data?: Record<string, unknown>
  ): Promise<TrackEventResponse> => {
    const response = await apiClient.post<TrackEventResponse>(
      '/sales_agent/track_event',
      {
        event_type: eventType,
        data: data || {},
      }
    );
    return response.data;
  },

  /**
   * Track when user clicks a CTA in email/push/in_app modal
   * Call this to attribute clicks for conversion tracking
   */
  actionClicked: async (salesActionId: string): Promise<ActionClickedResponse> => {
    const response = await apiClient.post<ActionClickedResponse>(
      '/sales_agent/action_clicked',
      {
        sales_action_id: salesActionId,
      }
    );
    return response.data;
  },

  /**
   * Track conversion attribution after successful subscription
   * Call this after payment completes to attribute the conversion
   */
  trackConversion: async (
    salesActionId?: string
  ): Promise<TrackConversionResponse> => {
    const response = await apiClient.post<TrackConversionResponse>(
      '/sales_agent/track_conversion',
      salesActionId ? { sales_action_id: salesActionId } : {}
    );
    return response.data;
  },

  /**
   * Get personalized paywall data
   * Use this to display a customized paywall/pricing screen
   */
  getPaywallData: async (): Promise<PaywallData> => {
    const response = await apiClient.get<PaywallDataResponse>(
      '/sales_agent/paywall_data'
    );
    return response.data.data;
  },

  /**
   * Get lead score (debug/development only)
   */
  getLeadScore: async (): Promise<LeadScoreResponse> => {
    const response = await apiClient.get<LeadScoreResponse>(
      '/sales_agent/lead_score'
    );
    return response.data;
  },
};

export default salesApi;
